import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  getPatientConversations, getPatientConversationMessages, sendPatientMessage,
  getPatientUnreadCount, createPatientConversation, getPatientCareTeam,
  markPatientMessagesRead, setPatientTyping, getPatientTypingStatus
} from "@/lib/api";
import { 
  MessageSquare, Send, User, Loader2, RefreshCw, Plus, Clock, 
  CheckCheck, Check, UserCircle, Heart, Phone, Mail, ArrowLeft
} from "lucide-react";

export default function PatientMessaging() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const typingCheckIntervalRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [staffTyping, setStaffTyping] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  // New message modal
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [careTeam, setCareTeam] = useState([]);
  const [newConversation, setNewConversation] = useState({
    subject: "",
    message: ""
  });
  const [creatingConversation, setCreatingConversation] = useState(false);
  
  const isPatientOrCaregiver = user?.role && ["patient", "caregiver", "family"].includes(user.role);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/");
        return;
      }
      loadData();
    }
    
    // Cleanup on unmount
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (typingCheckIntervalRef.current) clearInterval(typingCheckIntervalRef.current);
    };
  }, [user, authLoading, navigate]);

  // Poll for typing status when conversation is selected
  useEffect(() => {
    if (selectedConversation?.id) {
      checkTypingStatus();
      typingCheckIntervalRef.current = setInterval(checkTypingStatus, 3000);
      
      // Mark messages as read
      markMessagesAsRead();
      
      return () => {
        if (typingCheckIntervalRef.current) clearInterval(typingCheckIntervalRef.current);
      };
    }
  }, [selectedConversation?.id]);

  const checkTypingStatus = async () => {
    if (!selectedConversation?.id) return;
    try {
      const status = await getPatientTypingStatus(selectedConversation.id);
      setStaffTyping(status.staff_typing);
    } catch (error) {
      // Silently fail - typing status is not critical
    }
  };

  const markMessagesAsRead = async () => {
    if (!selectedConversation?.id) return;
    try {
      await markPatientMessagesRead(selectedConversation.id);
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const handleTyping = async () => {
    if (!selectedConversation?.id) return;
    
    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      try {
        await setPatientTyping(selectedConversation.id, true);
      } catch (error) {
        // Silently fail
      }
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    
    // Set timeout to stop typing indicator after 3 seconds
    typingTimeoutRef.current = setTimeout(async () => {
      setIsTyping(false);
      try {
        await setPatientTyping(selectedConversation.id, false);
      } catch (error) {
        // Silently fail
      }
    }, 3000);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [convData, unreadData, teamData] = await Promise.all([
        getPatientConversations().catch(() => ({ conversations: [] })),
        getPatientUnreadCount().catch(() => ({ unread_count: 0 })),
        getPatientCareTeam().catch(() => ({ care_team: [] }))
      ]);
      setConversations(convData.conversations || []);
      setUnreadCount(unreadData.unread_count || 0);
      setCareTeam(teamData.care_team || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationMessages = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const data = await getPatientConversationMessages(conversation.id);
      setMessages(data.messages || []);
      // Refresh unread count
      const unreadData = await getPatientUnreadCount().catch(() => ({ unread_count: 0 }));
      setUnreadCount(unreadData.unread_count || 0);
    } catch (error) {
      console.error("Error loading messages:", error);
      toast.error("Failed to load messages");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await sendPatientMessage(selectedConversation.id, newMessage.trim());
      setNewMessage("");
      // Reload messages
      await loadConversationMessages(selectedConversation);
      toast.success("Message sent");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!newConversation.subject.trim() || !newConversation.message.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    setCreatingConversation(true);
    try {
      const result = await createPatientConversation(
        newConversation.subject.trim(),
        newConversation.message.trim()
      );
      toast.success("Conversation started! Your care team will respond soon.");
      setShowNewMessageModal(false);
      setNewConversation({ subject: "", message: "" });
      await loadData();
      // Open the new conversation
      if (result.conversation) {
        loadConversationMessages(result.conversation);
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast.error(error.response?.data?.detail || "Failed to start conversation");
    } finally {
      setCreatingConversation(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container-max px-6 py-12 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container-max px-6 py-12 text-center">
        <MessageSquare className="w-16 h-16 mx-auto mb-4 text-primary/30" />
        <h1 className="text-2xl font-serif mb-2">Message Your Care Team</h1>
        <p className="text-muted-foreground mb-6">Sign in to send messages to your assigned hospice staff</p>
        <Button onClick={() => navigate("/")}>Sign In</Button>
      </div>
    );
  }

  return (
    <div className="container-max px-6 py-8" data-testid="patient-messaging">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif font-medium flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary" />
            Messages
          </h1>
          <p className="text-muted-foreground">Communicate with your care team</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowNewMessageModal(true)} data-testid="new-message-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Message
          </Button>
        </div>
      </div>

      {/* Care Team Summary */}
      {careTeam.length > 0 && (
        <Card className="mb-6 bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-primary flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Your Care Team:
              </span>
              {careTeam.slice(0, 3).map((member, i) => (
                <div key={i} className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border">
                  <UserCircle className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{member.name}</span>
                  <Badge variant="secondary" className="text-xs">{member.role}</Badge>
                </div>
              ))}
              {careTeam.length > 3 && (
                <span className="text-sm text-muted-foreground">+{careTeam.length - 3} more</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                Conversations
                {unreadCount > 0 && (
                  <Badge className="bg-primary">{unreadCount} unread</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {conversations.length > 0 ? (
                  conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => loadConversationMessages(conv)}
                      className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium text-sm truncate">{conv.staff_name}</span>
                          </div>
                          <p className="text-sm font-medium text-foreground mt-1 truncate">{conv.subject}</p>
                          {conv.last_message && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">{conv.last_message}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-muted-foreground">
                            {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                          </span>
                          {conv.unread_count_patient > 0 && (
                            <Badge className="bg-primary text-white text-xs">{conv.unread_count_patient}</Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs mt-1">Start a new message to your care team</p>
                    <Button 
                      size="sm" 
                      className="mt-4"
                      onClick={() => setShowNewMessageModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New Message
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Message Thread */}
        <div className="lg:col-span-2">
          <Card className="h-[600px] flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <CardHeader className="border-b py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="lg:hidden"
                        onClick={() => setSelectedConversation(null)}
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </Button>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{selectedConversation.staff_name}</p>
                          {staffTyping && (
                            <span className="text-xs text-green-600 animate-pulse">typing...</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{selectedConversation.subject}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg, index) => {
                    const isOwn = msg.sender_id === user.id;
                    const isLastOwnMessage = isOwn && messages.filter(m => m.sender_id === user.id).slice(-1)[0]?.id === msg.id;
                    return (
                      <div key={msg.id || index} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                          isOwn 
                            ? 'bg-primary text-white rounded-br-sm' 
                            : 'bg-muted rounded-bl-sm'
                        }`}>
                          <p className="text-sm">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <span className={`text-xs ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                              {formatTime(msg.created_at)}
                            </span>
                            {isOwn && (
                              msg.read ? (
                                <CheckCheck className="w-3 h-3 text-blue-300" title={`Read ${msg.read_at ? formatTime(msg.read_at) : ''}`} />
                              ) : (
                                <Check className="w-3 h-3 text-white/50" title="Delivered" />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Typing Indicator */}
                  {staffTyping && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>

                {/* Message Input */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      disabled={sendingMessage}
                      data-testid="message-input"
                    />
                    <Button 
                      onClick={handleSendMessage} 
                      disabled={!newMessage.trim() || sendingMessage}
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium mb-2">Select a Conversation</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Choose a conversation from the list or start a new one
                  </p>
                  <Button onClick={() => setShowNewMessageModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Message
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* New Message Modal */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              New Message to Care Team
            </DialogTitle>
            <DialogDescription>
              Your message will be sent to your assigned care team staff.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject</label>
              <Input
                placeholder="What is this about?"
                value={newConversation.subject}
                onChange={(e) => setNewConversation(prev => ({ ...prev, subject: e.target.value }))}
                data-testid="new-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Message</label>
              <Textarea
                placeholder="Type your message here..."
                value={newConversation.message}
                onChange={(e) => setNewConversation(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                data-testid="new-message-input"
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p className="font-medium">Note:</p>
              <p>For urgent matters, please call the hospice line at 703-578-7108</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMessageModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateConversation}
              disabled={creatingConversation || !newConversation.subject.trim() || !newConversation.message.trim()}
              data-testid="create-conversation-btn"
            >
              {creatingConversation ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
